/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { environment } from '../../environments/environment.prod';
import { UserAccountModel } from 'starbase-launcher';
import { FortisSubscriptionService } from '../services/fortis-subscription.service';
import { ExternalScriptService } from '../services/external-script.service';
import { StarbaseNotificationObject, StarbaseNotificationSentiment, StarbaseNotificationService } from 'starbase-notification';
import { finalize, Subscription } from 'rxjs';
import { UserDataService } from '../services/user-data.service';

class CardInfo {
  public last_4: string;
  public exp_month: string;
  public exp_year: string;
  public card_brand: string;
}

declare const window: any;

@Component({
  selector: 'app-fortis-manage-payment',
  templateUrl: './fortis-manage-payment.component.html',
  styleUrls: ['./fortis-manage-payment.component.css']
})
export class FortisManagePaymentComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() userAccount: UserAccountModel;
  @Input() darkMode = false;
  @Input() tooltipDelay = 100;
  @Input() standaloneMode = true;
  @Input() newUserMode = true;
  @Output() hasValidCard = new EventEmitter<boolean>();

  public addCardMode = false;

  loadingCard = true;
  processingCard: boolean;

  cardData: CardInfo = null;
  credentialError = false;
  tokenCompleted = false;
  fortisCard: any;
  vaultId: string = null;

  removalConfirmOpen = false;
  cardLoadDelay = 1;

  emailFrom = environment.testMode ? environment.testSupportEmailAddress : environment.supportEmailAddress;

  captchaVerified = false;
  siteKey = environment.recaptcha.siteKey;

  private cardSub: Subscription;

  constructor(private fortisSvc: FortisSubscriptionService,
    private scriptSvc: ExternalScriptService,
    private userSvc: UserDataService,
    private notiSvc: StarbaseNotificationService) {
   }

   get ccExpired(): boolean {
    const checkDate: Date = new Date(new Date().getFullYear(), new Date().getMonth());
    try {
      return new Date(this.cardData.exp_year + '/' + this.cardData.exp_month) < checkDate;
    } catch {
      return false;
    }
  }
  get ccAlmostExpired(): boolean {
    try {
      return new Date(this.cardData.exp_year + '/' + this.cardData.exp_month) >= new Date(new Date().getFullYear(), new Date().getMonth())
        // eslint-disable-next-line max-len
        && new Date(this.cardData.exp_year + '/' + this.cardData.exp_month) <= new Date(new Date().getFullYear(), new Date().getMonth() + 1);
    } catch {
      return false;
    }
  }

  ngOnInit() {
    if (!this.userAccount) {
      throw new Error('Payment form: No valid user account was loaded.');
    }
    this.processingCard = false;
    this.addCardMode = false;
    this.removalConfirmOpen = false;
    if (!this.cardData) {
      this.loadCard(this.cardLoadDelay);
    } else {
      this.loadingCard = false;
    }
    console.log('Waiting on Fortis server...');

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://js.sandbox.fortis.tech/commercejs-v1.0.0.min.js';

    document.getElementsByTagName('head')[0].appendChild(script);
  }

  ngAfterViewInit() {
    if (this.newUserMode || !this.standaloneMode) {
      this.setupCardEntryForm();
    }
  }

  resolvedRecaptcha(event) {
    if (event) {
      this.userSvc.verifyRecaptcha(event).subscribe(result => {
        if (result) {
          this.captchaVerified = true;
          setTimeout(() => {
            this.setupCardEntryForm();
          }, 250);
        } else {
          this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
            'Failed Verification',
            'Please refresh and try again.', undefined, undefined,
            StarbaseNotificationSentiment.Error
          ));
        }
      });
    }
  }

  setupCardEntryForm() {
    this.fortisSvc.getClientToken().subscribe((token) => {
      this.cleanupCardSubscriptions();
      this.tokenCompleted = false;
      this.fortisCard = new window.Commerce.elements(token);
      this.fortisCard.create({
        container: '#payment',
        theme: 'default',
        environment: environment.testMode ? 'sandbox' : 'production',
        view: 'default',
        showReceipt: false,
        showSubmitButton: false,
        showValidationAnimation: false,
        fields : {
          billing: false
        }
      });

      this.fortisCard.on('validationError', (event) => {
        console.log(event);
      });

      this.fortisCard.on('error', (event) => {
        console.log(event);
      });

      this.fortisCard.on('done', (event) => {
        console.log(event);
        if (!this.processingCard) {
          this.tokenCompleted = true;
          this.processingCard = true;
          this.vaultId = event.data.id;
          this.savePaymentMethod(event.data.id);
        }
      });

      this.checkCardInfo(this.cardData);
    });
  }

  cleanupCardSubscriptions() {
    if (this.fortisCard) {
      this.fortisCard.off('validationError');
      this.fortisCard.off('error');
      this.fortisCard.off('done');
    }
  }

  ngOnDestroy() {
    if (this.cardSub) {
      this.cardSub.unsubscribe();
    }
    this.cleanupCardSubscriptions();
  }

  /** Attempts to load the payment method on file for this user.
   *
   * @param delay The time, in milliseconds, before the call to the server is made. Defaults to 1 (ms) */
  loadCard(delay = 1): void {
    setTimeout(() => {
      this.cardSub = this.fortisSvc.getRequisiteData<CardInfo>(this.fortisSvc.getCardUri, this.userAccount.uid, 0).subscribe(
        (card) => {
          this.checkCardInfo(card);
          this.loadingCard = false;
          this.cardLoadDelay = 1;
        },
        (e) => {
          console.error(e);
          this.cardData = null;
          this.hasValidCard.emit(false);
          this.addCardMode = true;
          this.loadingCard = false;
        }
      );
    }, delay);
  }

  private checkCardInfo(card: CardInfo): void {
    this.cardData = card;
    if (this.cardData == null) {
      this.hasValidCard.emit(false);
      this.addCardMode = true;
    } else {
      if (this.ccExpired) {
        this.hasValidCard.emit(false);
      } else {
        this.hasValidCard.emit(true);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  async handlePaymentMethodSubmission(event, paymentMethod): Promise<void> {
    event.preventDefault();
    if (this.credentialError) {
      return;
    }
    try {
      // disable the submit button as we await tokenization and make a save card detail request.
      this.processingCard = true;
      const token = await this.tokenize(paymentMethod);
      this.savePaymentMethod(token);

    } catch (e) {
      this.processingCard = false;
      console.error(e.message);
    }
  }

  private async tokenize(paymentMethod) {
    const tokenResult = await paymentMethod.tokenize();
    if (tokenResult.status === 'OK') {
      return tokenResult.token;
    } else {
      let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
      if (tokenResult.errors) {
        errorMessage += ` and errors: ${JSON.stringify(
          tokenResult.errors
        )}`;
      }
      throw new Error(errorMessage);
    }
  }

  private async initializeCard(payments) {
    const card = await payments.card();
    await card.attach('#card-container');

    return card;
  }

  private savePaymentMethod(vaultId): void {
    this.loadingCard = true;
    this.fortisSvc.saveFortisCard(this.userAccount.uid, vaultId, this.newUserMode)
      .pipe(finalize(() => this.ngOnInit()))
      .subscribe(
      (newCard: CardInfo) => {
        this.fortisCard.off('done');
        this.checkCardInfo(newCard);
        this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
          'New card saved',
          'Your new card was saved on file and will be applied to any existing subscriptions.', undefined, undefined,
          StarbaseNotificationSentiment.Success
        ));
        this.cardLoadDelay = 2500; // wait after card is saved to give change for Fortis data to settle.
      },
      (e) => {
        console.error(e);
        this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
          'Could not save new card',
          `Sorry, there was an error when attempting to update your payment information.
          Please try again or contact the Billing Department at ${this.emailFrom}.`,
          undefined, 10000,
          StarbaseNotificationSentiment.Error
        ));
        this.processingCard = false;
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  replaceCard(): void {
    if (this.tokenCompleted) {
      this.setupCardEntryForm();
    }
    this.addCardMode = true;
    this.hasValidCard.emit(false);
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  cancelAddCard(): void {
    this.addCardMode = false;
    if (this.cardData == null) {
      this.hasValidCard.emit(false);
    } else {
      if (this.ccExpired) {
        this.hasValidCard.emit(false);
      } else {
        this.hasValidCard.emit(true);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  removeCard(): void {
    if (this.userAccount.subscribed) { // check if card is linked to active sub
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Cannot remove card',
          `You cannot remove the card associated with your subscription.
           Press "Update card" to use a different card or cancel your subscription first.`,
          undefined, 10000,
          StarbaseNotificationSentiment.Error));
      this.removalConfirmOpen = false;
    } else {
      this.loadingCard = true;
      this.cardLoadDelay = 5000;
        this.fortisSvc.removeFortisCard(this.userAccount.uid).subscribe(
        // confirmed removal
          () => {
            this.notiSvc.postNotification(
              this.notiSvc.notificationBuilder(
                'Card removed',
                'Your card was removed and will not be charged.',
                undefined, 7500,
                StarbaseNotificationSentiment.Success));
            this.cardData = null;
            this.setupCardEntryForm();
          },
        (e) => {
          console.error(e);
          this.notiSvc.postNotification(
            this.notiSvc.notificationBuilder(
              'Error',
              `Removing your payment method failed. Please try again or contact the Billing Dept at ${this.emailFrom}.`,
              undefined, 10000,
              StarbaseNotificationSentiment.Error));
          this.loadCard(500);
        });
    }
  }
}
